import { useState, useMemo } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { usePublicFabbriche, usePublicMarchi, usePublicServizi } from "@/hooks/usePublicEntities";

interface SearchItem {
  label: string;
  href: string;
  category: string;
}

const STATIC_ITEMS: SearchItem[] = [
  { label: "Shop", href: "/shop", category: "Shop" },
];

export const SearchBar = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const { data: fabbriche, isLoading: loadingF } = usePublicFabbriche();
  const { data: marchi, isLoading: loadingM } = usePublicMarchi();
  const { data: servizi, isLoading: loadingS } = usePublicServizi();

  const isLoading = loadingF || loadingM || loadingS;

  const searchItems = useMemo<SearchItem[]>(() => {
    const items: SearchItem[] = [...STATIC_ITEMS];

    fabbriche?.forEach((f) => {
      items.push({ label: f.nome, href: `/fabbriche/${f.slug}`, category: "Fabbriche" });
    });

    marchi?.forEach((m) => {
      items.push({
        label: m.nome,
        href: m.has_internal_page ? `/brands/${m.slug}` : (m.website_url || `/brands/${m.slug}`),
        category: "Marchi",
      });
    });

    servizi?.forEach((s) => {
      items.push({ label: s.nome, href: `/progetti/${s.slug}`, category: "Servizi" });
    });

    return items;
  }, [fabbriche, marchi, servizi]);

  const filteredSuggestions = useMemo(() => {
    if (!query) return [];
    const q = query.toLowerCase();
    return searchItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q)
    );
  }, [query, searchItems]);

  const handleResultClick = (href: string) => {
    if (href.startsWith("http")) {
      window.open(href, "_blank");
    } else {
      navigate(href);
    }
    setOpen(false);
    setQuery("");
  };

  const popularItems = searchItems.slice(0, 6);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="touch-target">
          <Search className="w-5 h-5" />
          <span className="sr-only">Cerca</span>
        </Button>
      </DialogTrigger>
      
      <DialogContent className="w-[95vw] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Cerca
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setOpen(false)}
              className="h-6 w-6"
            >
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Cerca prodotti, servizi, marchi..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 touch-target"
              autoFocus
            />
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-4 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Caricamento...
            </div>
          )}
          
          {!isLoading && query.length > 0 && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {filteredSuggestions.length > 0 ? (
                filteredSuggestions.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleResultClick(item.href)}
                    className="w-full text-left p-3 rounded-lg hover:bg-muted transition-colors touch-target"
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{item.label}</span>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                        {item.category}
                      </span>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  Nessun risultato trovato per "{query}"
                </p>
              )}
            </div>
          )}
          
          {!isLoading && query.length === 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm text-muted-foreground">Suggerimenti popolari</h4>
              {popularItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleResultClick(item.href)}
                  className="w-full text-left p-3 rounded-lg hover:bg-muted transition-colors touch-target"
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{item.label}</span>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                      {item.category}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
